// ui-kit/table — enhance SEMUA tabel .data: header klik-untuk-sort + lebar kolom bisa di-drag.
// Diterapkan global (observer #content) + tiap modal (openModal). Idempoten (guard __enhanced).

import { kebijakan } from '@core/settings';

type SortTh = HTMLElement & { __sortDir?: 'asc' | 'desc' };

const IND_MON: Record<string, number> = { jan: 0, feb: 1, mar: 2, apr: 3, mei: 4, jun: 5, jul: 6, agu: 7, agt: 7, sep: 8, okt: 9, nov: 10, des: 11 };
/** Baris ringkasan (TOTAL / colspan) — TIDAK ikut di-sort, selalu ditaruh di bawah (review #1). */
const isSummaryRow = (r: HTMLElement): boolean =>
  !!r.querySelector('td[colspan]') || /^total\b/i.test((r.children[0]?.textContent ?? '').trim());

function sortTableBy(tbl: HTMLTableElement, ci: number, th: SortTh): void {
  const tb = tbl.querySelector('tbody'); if (!tb) return;
  const rows = (Array.from(tb.children) as HTMLElement[]).filter((r) => r.tagName === 'TR' && r.children.length > 1);
  const data = rows.filter((r) => !isSummaryRow(r));
  const others = rows.filter((r) => isSummaryRow(r)); // TOTAL/summary → dipin di bawah
  if (data.length < 2) return;
  const dir: 'asc' | 'desc' = th.__sortDir === 'asc' ? 'desc' : 'asc';
  tbl.querySelectorAll('thead th').forEach((o) => {
    (o as SortTh).__sortDir = undefined;
    const a = o.querySelector('.col-sort'); if (a) { a.textContent = '⇅'; (a as HTMLElement).style.opacity = '.3'; }
  });
  th.__sortDir = dir;
  const av = th.querySelector('.col-sort'); if (av) { av.textContent = dir === 'asc' ? '▲' : '▼'; (av as HTMLElement).style.opacity = '1'; }
  const cellVal = (r: HTMLElement): string => { const c = r.children[ci] as HTMLElement | undefined; return c ? (c.getAttribute('data-sort') ?? (c.textContent ?? '').trim()) : ''; };
  const parse = (raw: string): { n: number | null; s: string } => {
    if (/^\d{4}-\d{2}-\d{2}/.test(raw)) { const d = new Date(raw).getTime(); if (!isNaN(d)) return { n: d, s: raw.toLowerCase() }; }
    const md = raw.match(/^(\d{1,2})\s+([A-Za-z]{3})\s+(\d{4})/); // tanggal Indonesia "16 Jul 2026" (review #2)
    if (md) { const mo = IND_MON[md[2]!.toLowerCase()]; if (mo != null) return { n: new Date(+md[3]!, mo, +md[1]!).getTime(), s: raw.toLowerCase() }; }
    if (/[0-9]/.test(raw) && /^[\sRp.,%\-0-9]+$/i.test(raw)) { const f = parseFloat(raw.replace(/[^0-9,.\-]/g, '').replace(/\./g, '').replace(/,/g, '.')); if (!isNaN(f)) return { n: f, s: raw.toLowerCase() }; }
    return { n: null, s: raw.toLowerCase() };
  };
  data.sort((x, y) => { const px = parse(cellVal(x)), py = parse(cellVal(y)); const r = (px.n != null && py.n != null) ? px.n - py.n : (px.s < py.s ? -1 : (px.s > py.s ? 1 : 0)); return dir === 'asc' ? r : -r; });
  data.forEach((r) => tb.appendChild(r));
  others.forEach((r) => tb.appendChild(r)); // summary/TOTAL selalu di bawah
  (tbl as HTMLTableElement & { __repage?: () => void }).__repage?.(); // paginator re-slice pasca-sort (halaman dipertahankan)
}

/** Header klik → urutkan (auto-deteksi Rp/angka/tanggal/teks). Kolom "Aksi"/kosong dilewati. */
function makeSortable(tbl: HTMLTableElement): void {
  // `nosort` = tabel yang URUTANNYA bermakna, jadi mengurutkan ulang merusak isinya.
  // Contoh: Statement Hutang Supplier — kolom Saldo adalah saldo BERJALAN (saldo += debit − kredit),
  // sel-nya ikut pindah bersama barisnya sehingga baris terakhir bukan lagi saldo terkini.
  // Tanpa penanda ini header tetap tampak bisa diklik (kursor + panah ⇅) → justru mengundang salah baca.
  if (tbl.classList.contains('nosort')) return;
  tbl.querySelectorAll('thead th').forEach((th, ci) => {
    const label = (th.textContent ?? '').trim().toLowerCase();
    if (label === '' || label === 'aksi' || label.startsWith('total')) return;
    (th as HTMLElement).style.cursor = 'pointer'; th.setAttribute('title', 'klik untuk urutkan · tarik batas kanan untuk lebar');
    if (!th.querySelector('.col-sort')) {
      const ar = document.createElement('span');
      ar.className = 'col-sort'; ar.style.cssText = 'opacity:.3;font-size:9px;margin-left:3px'; ar.textContent = '⇅';
      th.appendChild(ar);
    }
    th.addEventListener('click', () => sortTableBy(tbl, ci, th as SortTh));
  });
}

/** Lebar tiap kolom bisa di-drag (tarik batas kanan header). Tak ganggu klik-sort (stopPropagation). */
function makeColumnsResizable(tbl: HTMLTableElement): void {
  const ths = tbl.querySelectorAll<HTMLElement>('thead th');
  ths.forEach((th, ci) => {
    if (ci === ths.length - 1 || th.querySelector('.col-resizer')) return; // kolom terakhir tak perlu handle
    th.style.position = 'relative';
    const rz = document.createElement('span');
    rz.className = 'col-resizer';
    th.appendChild(rz);
    rz.addEventListener('click', (e) => e.stopPropagation()); // jangan picu sort
    rz.addEventListener('mousedown', (e) => {
      e.preventDefault(); e.stopPropagation();
      const startX = e.clientX, startW = th.offsetWidth;
      const move = (ev: MouseEvent): void => { const w = Math.max(40, startW + ev.clientX - startX); th.style.width = w + 'px'; th.style.minWidth = w + 'px'; };
      const up = (): void => { document.removeEventListener('mousemove', move); document.removeEventListener('mouseup', up); };
      document.addEventListener('mousemove', move); document.addEventListener('mouseup', up);
    });
  });
}

// Pola No. Servis: OUTLET-yymmdd-XXXX (mis. PKP-260713-JY53) atau seed (SEED-DG02). Anchor ^…$ per-token via \b.
// HATI-HATI (maintenance): bila kelak menambah kolom bertajuk No/Nota/Tiket/Servis yang memuat token
// huruf-strip mirip (mis. "No. Seri" = SN-AB12-CD34, atau "…Servis" = LCD-OLED), TNO_RE bisa salah-link.
// Efeknya lunak (klik → getTicket 0-baris → toast), tapi kecualikan kolom itu bila muncul.
const TNO_RE = /\b[A-Z]{2,6}-[A-Z0-9]{2,8}(?:-[A-Z0-9]{2,6})?\b/;
/** Header kolom yang mungkin memuat nomor tiket (dipersempit lagi oleh TNO_RE di sisi nilai). */
const headerIsTicketCol = (label: string): boolean => /\b(no|nota|tiket|servis)\b/i.test(label);

/** Jadikan nomor tiket/nota/servis di tabel bisa diklik → buka detail tiket (semua menu). Idempoten.
 *  Aman: hanya kolom bertajuk No/Nota/Tiket/Servis DAN sel yang nilainya cocok pola nomor tiket. */
function linkifyTicketNos(tbl: HTMLTableElement): void {
  const heads = Array.from(tbl.querySelectorAll('thead th')).map((th) => (th.textContent ?? '').trim());
  const cols = heads.map((h, i) => (headerIsTicketCol(h) ? i : -1)).filter((i) => i >= 0);
  if (!cols.length) return;
  const tb = tbl.querySelector('tbody'); if (!tb) return;
  (Array.from(tb.children) as HTMLElement[]).forEach((tr) => {
    if (tr.tagName !== 'TR' || isSummaryRow(tr)) return;
    cols.forEach((ci) => {
      const td = tr.children[ci] as HTMLElement | undefined;
      // Sel aksi (tombol/input) dilewati. Sel yang KEBETULAN memuat link (mis. badge "🔄 Servis
      // Ulang · dari NO") TETAP diproses — kalau tidak, nomor tiket milik baris itu sendiri
      // kehilangan tautannya justru di baris yang paling perlu dibuka.
      if (!td || td.hasAttribute('colspan') || td.querySelector('button,input,select')) return;
      // Bungkus token nomor tiket di text-node pertama (pertahankan badge/elemen tetangga).
      const walker = document.createTreeWalker(td, NodeFilter.SHOW_TEXT);
      let node: Node | null;
      while ((node = walker.nextNode())) {
        if (node.parentElement?.closest('a')) continue;   // teks yang memang sudah jadi tautan
        const txt = node.nodeValue ?? '';
        const m = txt.match(TNO_RE);
        if (!m || m.index == null) continue;
        const no = m[0];
        const after = (node as Text).splitText(m.index);
        (after as Text).nodeValue = (after.nodeValue ?? '').slice(no.length);
        const a = document.createElement('a');
        a.className = 'tno-link'; a.dataset.no = no; a.textContent = no; a.title = 'Buka detail tiket';
        after.parentNode?.insertBefore(a, after);
        break; // satu link per sel cukup
      }
    });
  });
}

/**
 * Bungkus tabel dengan pembungkus geser-mendatar. Tanpa ini tabel lebar (10-15 kolom seperti
 * Sudah Diambil / Hutang Supplier) TERPOTONG di laptop kecil & tablet — kolom Aksi tak bisa diklik.
 * Dilakukan terpusat di sini supaya berlaku ke SEMUA tabel, bukan ditambal satu per satu di tiap view.
 */
function wrapScrollable(tbl: HTMLTableElement): void {
  const p = tbl.parentElement;
  if (!p || p.dataset.tblScroll === '1') return;          // sudah dibungkus oleh helper ini
  const style = p.getAttribute('style') ?? '';
  if (/overflow-x\s*:\s*(auto|scroll)/.test(style)) return; // view sudah menyediakan sendiri
  const wrap = document.createElement('div');
  wrap.dataset.tblScroll = '1';
  wrap.style.overflowX = 'auto';
  tbl.replaceWith(wrap);
  wrap.appendChild(tbl);
}

/**
 * Paginasi KLIEN terpusat (keputusan Apo 19 Jul): tabel panjang dipotong per halaman
 * (baris/hal dari Pengaturan → Kebijakan, default 30; 0 = mati). Baris TOTAL/summary selalu
 * tampil. Sort tetap jalan (sortTableBy memanggil __repage). Export TAK terpengaruh
 * (export membaca array data, bukan DOM). Ceiling: data tetap di-fetch penuh —
 * saat data ribuan, layar terpanas pindah paginasi server (fondasi ini tetap terpakai).
 */
function paginateTable(tbl: HTMLTableElement): void {
  const size = Math.max(0, Number(kebijakan().rowsPerPage ?? 30));
  if (!size) return;
  const tb = tbl.querySelector('tbody'); if (!tb) return;
  // Kontrak satu-pemilik-display (review #8): view yang menyaring baris in-place (mis. Antrian)
  // MENANDAI lewat dataset.pgHide — hanya paginator yang menulis style.display. Tanpa kontrak ini
  // keduanya saling menimpa: pindah halaman/sort menghidupkan lagi baris yang disaring filter.
  const dataRows = (): HTMLElement[] =>
    (Array.from(tb.children) as HTMLElement[]).filter((r) => r.tagName === 'TR' && !isSummaryRow(r) && r.dataset['pgHide'] !== '1');
  const hiddenRows = (): HTMLElement[] =>
    (Array.from(tb.children) as HTMLElement[]).filter((r) => r.tagName === 'TR' && r.dataset['pgHide'] === '1');
  if (dataRows().length <= size) return;

  const bar = document.createElement('div');
  bar.className = 'pgbar';
  // Bar ditaruh SETELAH pembungkus scroll — ikut terlihat tanpa scroll mendatar.
  const p1 = tbl.parentElement;
  const scrollHost = p1 && (p1.dataset['tblScroll'] === '1' || /overflow-x\s*:\s*(auto|scroll)/.test(p1.getAttribute('style') ?? ''));
  const host = scrollHost ? p1 : tbl; // bar SETELAH pembungkus scroll (punya ui-kit ATAU punya view) — jangan ikut tergeser scroll mendatar
  host.insertAdjacentElement('afterend', bar);

  let page = 0, all = false;
  const apply = (): void => {
    const rows = dataRows();
    const maxP = Math.max(1, Math.ceil(rows.length / size));
    if (page > maxP - 1) page = maxP - 1;
    rows.forEach((r, i) => { r.style.display = all || (i >= page * size && i < (page + 1) * size) ? '' : 'none'; });
    hiddenRows().forEach((r) => { r.style.display = 'none'; }); // baris tersaring filter: selalu sembunyi
    const btn = (label: string, active = false, dis = false): string =>
      `<button class="btn ${active ? 'primary' : ''}" data-pg="${label}" ${dis ? 'disabled' : ''} style="padding:2px 9px;font-size:12px">${label}</button>`;
    // Jendela nomor ±2 dari halaman aktif (1 … 4 5 6 … 12) agar bar tak meledak di data besar.
    const nums: (number | '…')[] = [];
    for (let i = 0; i < maxP; i++) {
      if (i === 0 || i === maxP - 1 || Math.abs(i - page) <= 2) nums.push(i);
      else if (nums[nums.length - 1] !== '…') nums.push('…');
    }
    bar.innerHTML = all
      ? `<span class="pg-info">${rows.length} baris (semua)</span> ${btn('Per halaman')}`
      : `${btn('‹', false, page === 0)} ${nums.map((n) => n === '…' ? '<span class="pg-info">…</span>' : btn(String(n + 1), n === page)).join(' ')} ${btn('›', false, page === maxP - 1)} <span class="pg-info">${page * size + 1}–${Math.min((page + 1) * size, rows.length)} dari ${rows.length}</span> ${btn('Semua')}`;
    bar.querySelectorAll<HTMLButtonElement>('button[data-pg]').forEach((el) => el.addEventListener('click', () => {
      const v = el.dataset['pg'] ?? '';
      if (v === '‹') page = Math.max(0, page - 1);
      else if (v === '›') page = Math.min(maxP - 1, page + 1);
      else if (v === 'Semua') all = true;
      else if (v === 'Per halaman') { all = false; page = 0; }
      else page = +v - 1;
      apply();
    }));
  };
  (tbl as HTMLTableElement & { __repage?: () => void }).__repage = apply;
  apply();
}

/** Enhance semua tabel.data di `root`: pembungkus geser + sort header + resize kolom + nomor tiket klik-ke-detail. Idempoten. */
export function enhanceTables(root: HTMLElement): void {
  root.querySelectorAll<HTMLTableElement & { __enhanced?: boolean }>('table.data').forEach((tbl) => {
    if (tbl.__enhanced) return; tbl.__enhanced = true;
    wrapScrollable(tbl);
    makeSortable(tbl);
    makeColumnsResizable(tbl);
    linkifyTicketNos(tbl);
    paginateTable(tbl);
  });
}
